import {
  getManufacturingOverheadPeriodSummary,
  getManufacturingOverheadPeriodVariance,
  setManufacturingOverheadPeriodAssumed,
} from "@/lib/manufacturing/overheadClient";
import { addMonths, paymentUsd } from "@/lib/manufacturing/overheadObligation";
import type {
  MfgOverheadPeriodBucketRow,
  MfgOverheadPeriodState,
  MfgOverheadPeriodSummary,
  MfgOverheadPeriodVarianceReport,
} from "@/lib/manufacturing/overheadTypes";

export const CARRY_FORWARD_REASON_PREFIX = "Carried forward from ";

type CarryRow = Pick<
  MfgOverheadPeriodBucketRow,
  "bucket_id" | "assumed_value_usd" | "actual_value_usd" | "assumed_update_reason"
> & { is_aggregate?: boolean };

export function carryForwardReason(priorPeriodStart: string): string {
  return `${CARRY_FORWARD_REASON_PREFIX}${priorPeriodStart} when this month opened.`;
}

function editedByHand(row: CarryRow): boolean {
  const hasAmount = row.assumed_value_usd != null || row.actual_value_usd != null;
  if (!hasAmount) return false;
  if (row.actual_value_usd != null) return true;
  return !row.assumed_update_reason.startsWith(CARRY_FORWARD_REASON_PREFIX);
}

export function monthCanReceiveCarry(periodState: MfgOverheadPeriodState, currentRows: CarryRow[]): boolean {
  if (periodState === "closed" || periodState === "closing") return false;
  return !currentRows.some(editedByHand);
}

export function standingPaymentsToCarry(
  periodState: MfgOverheadPeriodState,
  currentRows: CarryRow[],
  priorRows: Array<Pick<MfgOverheadPeriodBucketRow, "bucket_id" | "assumed_value_usd" | "actual_value_usd">>,
): Array<{ bucket_id: string; assumed_value_usd: number }> {
  if (!monthCanReceiveCarry(periodState, currentRows)) return [];
  const priorById = new Map(
    priorRows.map((row) => [row.bucket_id, paymentUsd(row.assumed_value_usd, row.actual_value_usd)]),
  );
  const carries: Array<{ bucket_id: string; assumed_value_usd: number }> = [];
  for (const row of currentRows) {
    if (row.is_aggregate) continue;
    if (row.assumed_value_usd != null || row.actual_value_usd != null) continue;
    const amount = priorById.get(row.bucket_id);
    if (amount == null) continue;
    carries.push({ bucket_id: row.bucket_id, assumed_value_usd: amount });
  }
  return carries;
}

export async function fillBlankOverheadMonth(
  plantId: string,
  periodStart: string,
  summary: MfgOverheadPeriodSummary,
  stillCurrent: () => boolean,
): Promise<{ summary: MfgOverheadPeriodSummary; variance: MfgOverheadPeriodVarianceReport | null; error: string | null }> {
  if (!stillCurrent() || !monthCanReceiveCarry(summary.period_state, summary.rows)) {
    return { summary, variance: null, error: null };
  }
  const priorPeriod = addMonths(periodStart, -1);
  const priorRes = await getManufacturingOverheadPeriodSummary({ plant_id: plantId, period_start: priorPeriod });
  if (!stillCurrent() || !priorRes.ok) return { summary, variance: null, error: null };
  const carries = standingPaymentsToCarry(summary.period_state, summary.rows, priorRes.data.summary.rows);
  if (carries.length === 0) return { summary, variance: null, error: null };

  const reason = carryForwardReason(priorPeriod);
  const saved = await Promise.all(
    carries.map((row) =>
      setManufacturingOverheadPeriodAssumed({
        plant_id: plantId,
        bucket_id: row.bucket_id,
        period_start: periodStart,
        assumed_value_usd: row.assumed_value_usd,
        assumed_update_reason: reason,
      }),
    ),
  );
  if (!stillCurrent()) return { summary, variance: null, error: null };
  const failed = saved.find((result) => !result.ok);
  const [again, varianceAgain] = await Promise.all([
    getManufacturingOverheadPeriodSummary({ plant_id: plantId, period_start: periodStart }),
    getManufacturingOverheadPeriodVariance({ plant_id: plantId, period_start: periodStart }),
  ]);
  if (!stillCurrent()) return { summary, variance: null, error: null };
  return {
    summary: again.ok ? again.data.summary : summary,
    variance: varianceAgain.ok ? varianceAgain.data.report : null,
    error: failed && !failed.ok ? failed.error : null,
  };
}
